import { Container, Grid, useMediaQuery, useTheme } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useSearchParams } from 'react-router'
import { AppDispatch } from '../../store'
import { UserActions } from '../../store/User'
import { StorageKeys, VerificationSelector } from '../../store/Verification'
import { WaterTestKitsActions } from '../../store/waterTestKitRequests'
import { ProfileHeader } from '../Header'
import { WaterTestKitResultsActions } from '../../store/waterTestKitResults'
import { WaterTestParamsActions } from '../../store/waterTestParams'
import { WaterInfoDrawer } from '../WaterInfoDrawer'
import { PubWaterSystemActions } from '../../store/publicWaterSystem/pubWaterSystem'
import { AuthModal } from '../Modals/AuthModal'
import { ShareResultsModal } from '../Modals/ShareResultsModal'
import { GuidedReportWrapper } from '../../views/Profile/GuidedReport/GuidedReportWrapper'
import { AppActions, AppSelector } from '../../store/app'
import { WaterQualityIndexActions } from '../../store/waterQualityIndex'
import { ViolationEnforcementActions } from '../../store/ViolationEnforcements'

export const ProfileLayout = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useDispatch<AppDispatch>()
  const [searchParams, setSearchParams] = useSearchParams()

  const { isVerified } = useSelector(VerificationSelector.getVerificationState)
  const isSharedView = useSelector(AppSelector.getIsSharedView)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)

  // Process share token synchronously so localStorage is set before children render
  const shareTokenParam = searchParams.get('share')
  if (shareTokenParam && !localStorage.getItem(StorageKeys.TOKEN)) {
    localStorage.setItem(StorageKeys.TOKEN, shareTokenParam)
    localStorage.setItem(StorageKeys.SHARE_TOKEN, shareTokenParam)
  }

  const handleDataLoad = useCallback(async () => {
    const token = localStorage.getItem(StorageKeys.TOKEN)
    const email: string | null = token
      ? (JSON.parse(atob(token.split('.')[1])) as { email: string }).email
      : localStorage.getItem(StorageKeys.VERIFIED_EMAIL)

    if (!email) return

    const userResult = await dispatch(UserActions.fetchUserAsync({ email }))
    const userId = UserActions.fetchUserAsync.fulfilled.match(userResult) ? userResult.payload._id : undefined

    await dispatch(WaterTestParamsActions.fetchWaterTestParamsAsync({options:{params: {$paging:false}}}))
    const testkits = await dispatch(WaterTestKitsActions.fetchUserWaterTestKitRequestsAsync({email, options:{params:{ $sort:'createdAt desc'}}}))

    // Fire WQI fetch in parallel with test kit results (non-blocking for profile load)
    if (userId) {
      dispatch(WaterQualityIndexActions.fetchWaterQualityIndexAsync({ userId }))
    }

    await dispatch(
      WaterTestKitResultsActions.fetchWaterTestKitResultsByUserAsync({
        email,
        options: {
          params: {
            $sort: 'createdAt desc',
            $paging: false,
          },
        },
      })
    )
    if(WaterTestKitsActions.fetchUserWaterTestKitRequestsAsync.fulfilled.match(testkits)) {
      const pwsid = testkits.payload[0].utility!
      dispatch(PubWaterSystemActions.setSelectedWaterSystem(pwsid))
      await dispatch(PubWaterSystemActions.fetchPubWaterSystemAsync({ pwsid }))
      // Fetch violations data for Story Mode Screen 4 (non-blocking)
      dispatch(ViolationEnforcementActions.fetchViolationsEnforcementsGroupedByViolationAsync({ pwsId: pwsid }))
    }
    setDataLoaded(true)
  }, [dispatch])

  const handleAuthSuccess = useCallback(() => {
    setShowAuthModal(false)
    handleDataLoad()
  }, [handleDataLoad])

  // Dispatch shared view state and clean up URL
  useEffect(() => {
    if (shareTokenParam) {
      dispatch(AppActions.setSharedView(true))
      searchParams.delete('share')
      setSearchParams(searchParams, { replace: true })
    } else if (localStorage.getItem(StorageKeys.SHARE_TOKEN)) {
      dispatch(AppActions.setSharedView(true))
    }
  }, [shareTokenParam, searchParams, setSearchParams, dispatch])

  useEffect(() => {
    const token = localStorage.getItem(StorageKeys.TOKEN)
    const isShare = !!localStorage.getItem(StorageKeys.SHARE_TOKEN)
    if (!token && !isVerified && !isShare) {
      setShowAuthModal(true)
    } else if ((token || isVerified) && !dataLoaded) {
      handleDataLoad()
    }
  }, [isVerified, dataLoaded, handleDataLoad, isSharedView])
  return (
    <Container maxWidth="lg" sx={{ height: '100%' }}>
      {!isSharedView && !localStorage.getItem(StorageKeys.SHARE_TOKEN) && (
        <AuthModal open={showAuthModal} onSuccess={handleAuthSuccess} />
      )}
      <ShareResultsModal />
      {/* SIPP-653: Story Mode overlay — fires on any profile route, not just Recommendations */}
      <GuidedReportWrapper />
      <ProfileHeader />
      <Grid
        container
        direction="column"
        spacing={0}
        height="100%"
        wrap="nowrap"
        sx={{ maxHeight: '100vh' }}
      >

          <Outlet />
        <WaterInfoDrawer
          anchor={isMobile ? 'bottom' : 'right'}
          slotProps={{
            paper: {
              sx: {
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                },
                width: isMobile ? '100%': '560px',
                height: isMobile ? '90%' : '100%',
                borderRadius: 0,
                //overflow: 'hidden',
                zIndex: theme.zIndex.drawer + 100,
                position: 'absolute',
                borderTopLeftRadius: isMobile ? '24px' : 0,
                borderTopRightRadius: isMobile ? '24px' : 0,
                bgcolor: theme.palette.success.contrastText,
              }
            }}}

        />
      </Grid>
    </Container>
  )
}
